import {Dayjs} from "dayjs";
import React, {CSSProperties, MutableRefObject} from "react";
import {createDateRangePresets, DateRangePresetId} from "./DateRangePresets";

const datePresets = createDateRangePresets();
const ACTIVE_DATE_RANGE_PRESET_COLOR = "#1677ff";
const dateRangePresetLabelStyle: CSSProperties = {display: "block"};
const activeDateRangePresetLabelStyle: CSSProperties = {
    ...dateRangePresetLabelStyle,
    fontWeight: 600,
    color: ACTIVE_DATE_RANGE_PRESET_COLOR
};

export type DateRangePresetSelectionHandler = (presetId: DateRangePresetId, range: [Dayjs, Dayjs]) => void;

export function createDateRangePickerPresets(activePresetId: DateRangePresetId | undefined,
                                             pendingDatePresetIdRef: MutableRefObject<DateRangePresetId | undefined>,
                                             onPresetSelected?: DateRangePresetSelectionHandler) {
    return datePresets.map(preset => {
        const [from, to] = preset.value();
        const active = activePresetId === preset.id;

        return {
            label: (
                <DateRangePresetLabel
                    active={active}
                    presetId={preset.id}
                    range={[from, to]}
                    pendingDatePresetIdRef={pendingDatePresetIdRef}
                    onPresetSelected={onPresetSelected}
                >
                    {preset.label}
                </DateRangePresetLabel>
            ),
            value: [from, to] as [Dayjs, Dayjs]
        };
    });
}

export function takePendingDatePresetId(pendingDatePresetIdRef: MutableRefObject<DateRangePresetId | undefined>) {
    const pendingDatePresetId = pendingDatePresetIdRef.current;

    pendingDatePresetIdRef.current = undefined;

    return pendingDatePresetId;
}

function clearPendingDatePresetIdAfterRangePickerChange(pendingDatePresetIdRef: MutableRefObject<DateRangePresetId | undefined>,
                                                        presetId: DateRangePresetId) {
    setTimeout(() => {
        if (pendingDatePresetIdRef.current === presetId) {
            pendingDatePresetIdRef.current = undefined;
        }
    }, 0);
}

interface DateRangePresetLabelProps {
    active: boolean;
    presetId: DateRangePresetId;
    range: [Dayjs, Dayjs];
    pendingDatePresetIdRef: MutableRefObject<DateRangePresetId | undefined>;
    onPresetSelected?: DateRangePresetSelectionHandler;
    children: React.ReactNode;
}

function DateRangePresetLabel(props: DateRangePresetLabelProps) {
    const labelRef = React.useRef<HTMLSpanElement>(null);
    const cancelledPressTimeoutRef = React.useRef<number>();

    React.useEffect(() => {
        // AntD only lets us customize the preset label, while the clickable target is the parent row.
        // Bind to the row so clicks on row padding are tracked as explicit preset selections too.
        const presetRow = labelRef.current?.closest("li");

        if (!presetRow) {
            return;
        }

        const clearCancelledPresetPress = () => {
            if (props.pendingDatePresetIdRef.current === props.presetId) {
                props.pendingDatePresetIdRef.current = undefined;
            }

            cancelledPressTimeoutRef.current = undefined;
        };

        // A cancelled press sets the pending preset on mousedown but never produces click/onChange.
        // Clear it after mouseup unless a normal click consumes it first.
        const scheduleCancelledPresetPressClear = () => {
            if (cancelledPressTimeoutRef.current !== undefined) {
                window.clearTimeout(cancelledPressTimeoutRef.current);
            }

            cancelledPressTimeoutRef.current = window.setTimeout(clearCancelledPresetPress, 0);
            window.removeEventListener("mouseup", scheduleCancelledPresetPressClear);
        };

        const markPresetSelection = () => {
            props.pendingDatePresetIdRef.current = props.presetId;

            if (cancelledPressTimeoutRef.current !== undefined) {
                window.clearTimeout(cancelledPressTimeoutRef.current);
            }

            window.removeEventListener("mouseup", scheduleCancelledPresetPressClear);
            window.addEventListener("mouseup", scheduleCancelledPresetPressClear, {once: true});
        };

        const applyPresetSelection = () => {
            if (cancelledPressTimeoutRef.current !== undefined) {
                window.clearTimeout(cancelledPressTimeoutRef.current);
                cancelledPressTimeoutRef.current = undefined;
            }

            window.removeEventListener("mouseup", scheduleCancelledPresetPressClear);
            props.onPresetSelected?.(props.presetId, props.range);
            clearPendingDatePresetIdAfterRangePickerChange(props.pendingDatePresetIdRef, props.presetId);
        };

        presetRow.addEventListener("mousedown", markPresetSelection);
        presetRow.addEventListener("click", applyPresetSelection);

        return () => {
            presetRow.removeEventListener("mousedown", markPresetSelection);
            presetRow.removeEventListener("click", applyPresetSelection);
            window.removeEventListener("mouseup", scheduleCancelledPresetPressClear);

            if (cancelledPressTimeoutRef.current !== undefined) {
                window.clearTimeout(cancelledPressTimeoutRef.current);
                cancelledPressTimeoutRef.current = undefined;
            }
        };
    }, [props.pendingDatePresetIdRef, props.presetId, props.range, props.onPresetSelected]);

    return (
        <span ref={labelRef} style={props.active ? activeDateRangePresetLabelStyle : dateRangePresetLabelStyle}>
            {props.children}
        </span>
    );
}
